import React, { useCallback, useState } from 'react';
import { isEmpty } from 'lodash';

import { ChatPanelView } from '~/components/AiChat/components/ChatPanel';
import { useChatController } from '~/components/AiChat/controller/ChatControllerContext';
import { useChatMixpanel } from '~/components/AiChat/hooks/useChatMixpanel';
import NewChatIcon from '~/components/AiChat/icons/NewChatIcon';
import PageMenuIcon from '~/components/AiChat/icons/PageMenuIcon';
import SearchIcon from '~/components/AiChat/icons/SearchIcon';
import { CLIVE_GRADIENTS } from '~/components/Clive/cliveRegistry';
import MiniDrawer from '~/components/core/MiniDrawer';
import { CLIVE_CHAT_MIXPANEL_EVENTS } from '~/pocs/mixpanel';
import cn from '~/Utils/cn';

import { FullScreenChatHistoryList } from './components/FullScreenChatHistoryList';
import { SearchChatHistoryDialog } from './components/SearchChatHistoryDialog';
import { DRAWER_ITEM_KEYS, type DrawerItemKey } from './constants';

const NO_HISTORY_SELECT = async () => {};

/**
 * The full-screen chat of the controller a `ChatControllerProvider` above it supplies: the sidebar (New chat, and
 * Search and the previous chats where the controller offers `history`) beside the chat's title and transcript.
 */
export const FullScreenChatLayout: React.FC = () => {
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState<DrawerItemKey | ''>('');
  const [isSearchDialogOpen, setIsSearchDialogOpen] = useState(false);
  const { exchanges, startNewConversation, history, title } = useChatController({ required: true });
  const { trackChatEvent } = useChatMixpanel();

  const hasMessages = !isEmpty(exchanges);

  const handleItemSelect = async (itemKey: string) => {
    if (itemKey === DRAWER_ITEM_KEYS.NEW_CHAT) {
      trackChatEvent(CLIVE_CHAT_MIXPANEL_EVENTS.CHAT_NEW_CLICKED, { has_messages: hasMessages });
      await startNewConversation?.();
    }
    if (itemKey === DRAWER_ITEM_KEYS.SEARCH) {
      trackChatEvent(CLIVE_CHAT_MIXPANEL_EVENTS.CHAT_SEARCH_OPENED);
      setIsSearchDialogOpen(true);
    }
    setSelected(itemKey as DrawerItemKey);
  };

  const handleSearchDialogClose = useCallback(() => {
    trackChatEvent(CLIVE_CHAT_MIXPANEL_EVENTS.CHAT_SEARCH_CLOSED);
    setIsSearchDialogOpen(false);
  }, [trackChatEvent]);

  const handleDrawerToggle = useCallback(
    (isOpen: boolean) => {
      trackChatEvent(CLIVE_CHAT_MIXPANEL_EVENTS.CHAT_DRAWER_TOGGLED, { is_open: isOpen });
      setOpen(isOpen);
    },
    [trackChatEvent]
  );

  const handleChatSelect = history?.open ?? NO_HISTORY_SELECT;

  return (
    <div className={cn('flex h-full', CLIVE_GRADIENTS.page)}>
      <MiniDrawer
        open={open}
        onToggle={handleDrawerToggle}
        selectedKey={selected}
        onItemSelect={handleItemSelect}
        className={cn({ 'bg-white': open, 'bg-white/30 shadow-sm': !open })}
        itemClassName="rounded-lg bg-white hover:bg-white/50 border-0 text-blue-600 mx-16 my-8 pl-[10px] cursor-pointer"
        toggleButtonClassName="rounded-lg bg-white hover:bg-white/50 border-0 text-blue-600 mt-24"
        ToggleIcon={PageMenuIcon}
        iconSize={16}
      >
        <MiniDrawer.Item
          itemKey={DRAWER_ITEM_KEYS.NEW_CHAT}
          Icon={NewChatIcon}
          label="New Chat"
          disabled={!hasMessages || !startNewConversation}
        />
        {history && <MiniDrawer.Item itemKey={DRAWER_ITEM_KEYS.SEARCH} Icon={SearchIcon} label="Search" />}
        {history && (
          <MiniDrawer.Custom>
            {({ isExpanded }) => (
              <FullScreenChatHistoryList
                history={history}
                currentTitle={title}
                onChatSelect={handleChatSelect}
                isExpanded={isExpanded}
              />
            )}
          </MiniDrawer.Custom>
        )}
      </MiniDrawer>
      <main className="min-h-0 flex-1 overflow-hidden">
        <ChatPanelView />
      </main>
      {history && (
        <SearchChatHistoryDialog history={history} open={isSearchDialogOpen} onClose={handleSearchDialogClose} />
      )}
    </div>
  );
};
